import { useEffect, useState } from 'react'

import { api, can, refusalReason, type VendorDocument } from '../lib/api'
import { docTypeWords } from '../lib/documentTypes'
import { Icon } from './ui'

// The documents a vendor has given us, on the overview: what is on file and the
// way to add one.
//
// # WHY IT NO LONGER UPLOADS
//
// This card had its own upload form, which never asked when a document starts
// or expires, while the Contracts part had a second form that did and warned
// that without the date "the certificate lapses silently" (release 3.2,
// removal item 14). Both wrote the same table. A document is now added in one
// place, the form under Contracts, which always asks the dates; this card lists
// what is on file and opens that form.

function dateWord(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function VendorDocumentsCard({ vendorId, onAdd }: { vendorId: string; onAdd: () => void }) {
  const [docs, setDocs] = useState<VendorDocument[] | null>(null)

  useEffect(() => {
    let live = true
    api
      .vendorDocuments(vendorId)
      .then((r) => {
        if (live) setDocs(r.documents)
      })
      .catch(() => {
        if (live) setDocs([])
      })
    return () => {
      live = false
    }
  }, [vendorId])

  const allowed = can('evidence.upload')
  return (
    <section className="p1-card2 docs-card" style={{ marginTop: 12 }}>
      <div className="p1-head" style={{ marginBottom: 6 }}>
        <h2 style={{ margin: 0 }}>Documents {docs && docs.length > 0 && <span className="chip">{docs.length}</span>}</h2>
        <div className="p1-actions docs-upload">
          <button
            type="button"
            className="button"
            disabled={!allowed}
            title={allowed ? 'Opens the form under Contracts, which asks the dates' : refusalReason('evidence.upload')}
            onClick={onAdd}
          >
            <Icon name="file" size={15} /> Add a document
          </button>
        </div>
      </div>
      {docs === null ? (
        <p className="muted">Loading</p>
      ) : docs.length === 0 ? (
        <div className="p1-empty">
          <span>Nothing on file yet</span>
        </div>
      ) : (
        <ul className="docs-list">
          {docs.map((d) => (
            <li key={d.id}>
              <span className="docs-icon">
                <Icon name="file" size={16} />
              </span>
              <span className="docs-main">
                <strong>{d.title}</strong>
                <span className="cell-sub">
                  {docTypeWords(d.docType)}, added {dateWord(d.createdAt)} by {d.uploadedBy}
                  {typeof d.acceptedCount === 'number' && d.acceptedCount > 0 ? ', ' + d.acceptedCount + ' answers taken from it' : ''}
                </span>
              </span>
              <a className="button ghost" href={'#/vendor/' + vendorId + '/full/contracts'}>
                Read it for answers
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
